import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const myobInvoiceSchema = z.object({
  objectType: z.string().min(1, 'Object type is required'),
  objectId: z.string().min(1, 'Object ID is required'),
  invoiceNumber: z.string().min(1, 'Invoice number is required'),
  amount: z.string().min(1, 'Amount is required'),
});

type MyobInvoiceForm = z.infer<typeof myobInvoiceSchema>;

export function MFilesMyobPage() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MyobInvoiceForm>({
    resolver: zodResolver(myobInvoiceSchema),
  });

  const upsertMutation = useMutation({
    mutationFn: (data: MyobInvoiceForm) =>
      api.post('/api/connections/mfiles/upsertMyOBInvoice', data),
    onSuccess: () => {
      toast.success('Invoice upserted to MYOB successfully');
      reset();
    },
    onError: () => toast.error('Failed to upsert invoice to MYOB'),
  });

  async function onSubmit(data: MyobInvoiceForm) {
    upsertMutation.mutate(data);
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/connections/mfiles">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">M-Files → MYOB</h1>
          <p className="text-gray-500">Upsert invoice from M-Files to MYOB</p>
        </div>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-lg">Upsert MYOB Invoice</CardTitle>
          <CardDescription>Create or update an invoice in MYOB via M-Files</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="objectType">Object Type</Label>
              <Input id="objectType" {...register('objectType')} />
              {errors.objectType && <p className="text-sm text-red-500">{errors.objectType.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="objectId">Object ID</Label>
              <Input id="objectId" {...register('objectId')} />
              {errors.objectId && <p className="text-sm text-red-500">{errors.objectId.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceNumber">Invoice Number</Label>
              <Input id="invoiceNumber" {...register('invoiceNumber')} />
              {errors.invoiceNumber && <p className="text-sm text-red-500">{errors.invoiceNumber.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="myob-amount">Amount</Label>
              <Input id="myob-amount" placeholder="0.00" {...register('amount')} />
              {errors.amount && <p className="text-sm text-red-500">{errors.amount.message}</p>}
            </div>
            <Button type="submit" disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? 'Upserting...' : 'Upsert to MYOB'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}